<template>
  <div class="bg-white min-h-screen">
    <!-- Hero Banner Section -->
    <section class="bg-gradient-to-br from-[#CB2530] to-[#B41923] relative overflow-hidden pt-24 lg:pt-32 pb-12 lg:pb-16 min-h-[320px] lg:min-h-[400px] flex items-center">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 w-full">
        <h1 class="text-4xl md:text-5xl font-medium text-white mb-6 tracking-tight leading-tight whitespace-pre-line">{{ equipConfigs.equip_banner_title || $t('common.equipment.title') }}</h1>
        <div class="space-y-4">
          <p class="text-xl text-white/85 max-w-3xl mx-auto leading-relaxed whitespace-pre-line">
            {{ equipConfigs.equip_banner_desc }}
          </p>
        </div>
      </div>
    </section>

    <!-- Statistics Cards Section -->
    <section class="py-20 bg-white border-b border-slate-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div v-for="(stat, index) in topStats" :key="index" 
               class="text-center p-6 bg-gradient-to-br from-[#FDF3F4] to-[#FBEEEF] rounded-xl border border-[#F2D6D8]"
          >
            <div class="text-[#C6202B] mb-4 flex items-center justify-center">
              <img v-if="isExternalUrl(stat.iconName)" :src="processImageUrl(stat.iconName)" class="w-10 h-10 object-contain" />
              <i v-else :class="getIconClass(stat.iconName)" class="text-4xl"></i>
            </div>
            <div class="text-4xl text-slate-900 mb-2 font-medium">{{ stat.value }}</div>
            <div class="text-slate-600">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Equipment Detail Sections -->
    <section class="py-20 bg-slate-50 border-b border-slate-200">
      <div class="container-custom">
        <div class="text-center mb-12">
          <h2 class="text-3xl text-slate-900 mb-4 tracking-tight font-medium">{{ $t('common.equipment.coreEquip') }}</h2>
          <p class="text-slate-600 text-base">{{ $t('common.equipment.coreEquipSubtitle') }}</p>
        </div>
        
        <div class="space-y-12">
          <div v-for="(item, index) in equipmentList" :key="index" 
               :class="['grid grid-cols-1 lg:grid-cols-2 gap-8 items-center', index % 2 === 1 ? 'lg:grid-flow-dense' : '']">
            <!-- Image Part -->
            <div :class="[index % 2 === 1 ? 'lg:col-start-2' : '']">
              <div class="relative group">
                <div class="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl bg-slate-200">
                  <img :src="processImageUrl(item.imageUrl)" :alt="item.title" class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                </div>
                <div class="absolute -bottom-4 -right-4 w-32 h-32 bg-[#C6202B]/10 rounded-2xl -z-10"></div>
              </div>
            </div>
            <!-- Text Part -->
            <div :class="['space-y-4 lg:space-y-6', index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : '']">
              <h3 class="text-2xl text-slate-900 mb-4 tracking-tight font-medium">{{ item.title }}</h3>
              <p class="text-slate-700 mb-6 leading-relaxed text-base font-normal whitespace-pre-line">{{ item.description }}</p>
              
              <!-- Specific Stats for Online Inspection -->
              <div v-if="parseSubStats(item.subStats).length > 0" class="grid grid-cols-2 gap-4 pt-4 lg:pt-6">
                <div v-for="(sub, sIdx) in parseSubStats(item.subStats)" :key="sIdx" 
                     class="bg-white p-4 lg:p-5 rounded-xl border border-slate-100 shadow-md flex flex-col space-y-1 hover:shadow-lg transition-all duration-300">
                  <div class="text-slate-500 text-xs font-medium tracking-widest uppercase">{{ sub.label }}</div>
                  <div class="text-[#C6202B] text-xl font-medium tracking-tight">{{ sub.value }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Technical Advantages Section -->
    <section class="py-20 bg-white">
      <div class="container-custom">
        <div class="text-center mb-12 space-y-4">
          <h2 class="text-3xl text-slate-900 mb-4 tracking-tight font-medium">{{ $t('common.equipment.techAdvantage') }}</h2>
          <p class="text-slate-600 text-base max-w-3xl mx-auto leading-relaxed">{{ $t('common.equipment.techAdvantageSubtitle') }}</p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          <div v-for="(adv, index) in technicalAdvantages" :key="index" 
               class="bg-gradient-to-br from-slate-50 to-[#FDF3F4] p-8 rounded-xl border border-[#F2D6D8] hover:shadow-lg transition-all duration-500 flex flex-col items-start text-left group">
            <div class="text-[#C6202B] mb-4 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              <img v-if="isExternalUrl(adv.iconName)" :src="processImageUrl(adv.iconName)" class="w-12 h-12 object-contain" />
              <i v-else :class="getIconClass(adv.iconName)" class="text-4xl"></i>
            </div>
            <div class="space-y-3">
              <h3 class="text-xl text-slate-900 mb-3 font-medium tracking-tight">{{ adv.title }}</h3>
              <p class="text-slate-700 leading-relaxed font-normal">{{ adv.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import { getPublicEquipmentAll } from '@/api/equipment'

export default {
  name: 'EquipmentView',
  props: {
    preloadedData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      equipConfigs: {
        equip_banner_title: '',
        equip_banner_subtitle: '',
        equip_banner_desc: ''
      },
      topStats: [],
      equipmentList: [],
      technicalAdvantages: []
    }
  },
  watch: {
    preloadedData: {
      handler(newVal) {
        if (newVal) {
          this.applyPreloadedData(newVal);
        }
      },
      immediate: true
    }
  },
  created() {
    this.fetchData()
    this.$root.$on('language-changed', this.fetchData)
  },
  beforeDestroy() {
    this.$root.$off('language-changed', this.fetchData)
  },
  methods: {
    applyPreloadedData(data) {
      // 更新配置
      const isEn = this.$i18n && this.$i18n.locale === 'en'
      Object.keys(this.equipConfigs).forEach(key => {
        const configKey = isEn ? `${key}_en` : key
        if (data[configKey] !== undefined) {
          this.equipConfigs[key] = data[configKey]
        } else if (data[key] !== undefined) {
          this.equipConfigs[key] = data[key]
        }
      })
      
      // 更新统计
      if (data.statList) {
        this.topStats = data.statList.filter(s => s.statType === 'top')
      }
      
      // 更新设备
      if (data.equipmentList && data.equipmentList.length > 0) {
        this.equipmentList = data.equipmentList
      }
      
      // 更新优势
      if (data.advantageList && data.advantageList.length > 0) {
        this.technicalAdvantages = data.advantageList
      }
    },
    async fetchData() {
      try {
        const res = await getPublicEquipmentAll()
        if (res.code === 200) {
          this.applyPreloadedData(res.data);
        }
      } catch (error) {
        console.error('Failed to fetch equipment data:', error)
      }
    },
    processImageUrl(url) {
      if (!url) return require('../assets/images/about-company.png')
      if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url
      const baseUrl = process.env.VUE_APP_BASE_API || 'http://localhost:8080'
      return baseUrl + url
    },
    isExternalUrl(url) {
      return url && (url.startsWith('/') || url.startsWith('http'))
    },
    getIconClass(name) {
      const iconMap = {
        Cpu: 'el-icon-cpu',
        Microscope: 'el-icon-search',
        Activity: 'el-icon-s-opportunity',
        ShieldCheck: 'el-icon-s-check',
        Target: 'el-icon-bangzhu',
        Layout: 'el-icon-menu',
        History: 'el-icon-time',
        Zap: 'el-icon-bangzhu',
        Microscope: 'el-icon-discover',
        Factory: 'el-icon-office-building'
      }
      return iconMap[name] || 'el-icon-star-on'
    },
    parseSubStats(json) {
      if (!json) return []
      try {
        return typeof json === 'string' ? JSON.parse(json) : json
      } catch (e) {
        console.error('Parse subStats error:', e)
        return []
      }
    }
  }
}
</script>

<style scoped>
.container-custom {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
</style>
